import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { COLLECTIONS } from '../../types/firebase';
import MainLayout from '../Layout/MainLayout';

import { useFeedData } from './Feed/useFeedData';
import {
  SystemStatusCard,
  MapCard,
  TrendChartCard,
  SiteConditionCard,
  RecentReportsCard,
} from './Feed/FeedCards';

import './Feed/Feed.css';

const ProfilePage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const feed = useFeedData();

  // Check if company profile exists on component mount
  useEffect(() => {
    const checkCompanyProfile = async () => {
      if (!userProfile?.masterOwnerID) {
        setLoading(false);
        return;
      }

      try {
        const profileRef = doc(db, COLLECTIONS.COMPANIES, userProfile.masterOwnerID);
        const profileSnap = await getDoc(profileRef);

        if (!profileSnap.exists()) {
          navigate('/create-profile');
          return;
        }

        setLoading(false);
      } catch (error) {
        console.error('Error checking company profile:', error);
        navigate('/create-profile');
      }
    };

    if (userProfile) {
      checkCompanyProfile();
    }
  }, [userProfile, navigate]);

  if (loading) {
    return (
      <MainLayout title="Feed">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Feed">
      <div className="feed-content">
        <div className="feed-grid">
          <div className="feed-top-row">
            <SystemStatusCard
              totalReports={feed.totalReports}
              skimmerCount={feed.skimmerCount}
              noSkimmerCount={feed.noSkimmerCount}
            />
            {feed.loading ? (
              <Box className="glass-panel data-card map-card" display="flex" alignItems="center" justifyContent="center" minHeight={260}>
                <CircularProgress size={40} />
              </Box>
            ) : (
              <MapCard
                sites={feed.sites}
                siteStatusMap={feed.siteStatusMap}
                reports={feed.reports}
                viewerMasterOwnerId={userProfile?.masterOwnerID}
              />
            )}
          </div>

          <div className="feed-middle-row">
            <TrendChartCard trendData={feed.trendData} />
            {feed.loading ? (
              <Box className="glass-panel data-card site-condition-card" display="flex" alignItems="center" justifyContent="center" minHeight={200}>
                <CircularProgress size={40} />
              </Box>
            ) : (
              <SiteConditionCard sites={feed.sites} siteStatusMap={feed.siteStatusMap} />
            )}
          </div>

          <div className="feed-bottom-row">
            {feed.loading ? (
              <Box className="glass-panel data-card recent-reports-card" display="flex" alignItems="center" justifyContent="center" minHeight={200}>
                <CircularProgress size={40} />
              </Box>
            ) : (
              <RecentReportsCard
                reports={feed.reports}
                viewerMasterOwnerId={userProfile?.masterOwnerID}
              />
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ProfilePage;
